import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { restaurantConfig } from '../config/restaurantConfig';

interface ReviewProps {
  name: string;
  image: string;
  comment: {
    en: string;
    pt: string;
  };
  location: {
    en: string;
    pt: string;
  };
  rating: number;
  sourceUrl: string;
}

interface SwiperTestimonialsProps {
  reviews: ReviewProps[];
  language: string;
  onReviewClick: (review: ReviewProps, e: React.MouseEvent) => void;
}

const SwiperTestimonials: React.FC<SwiperTestimonialsProps> = ({ 
  reviews, 
  language, 
  onReviewClick 
}) => {
  const [loadedImages, setLoadedImages] = useState<{[key: string]: boolean}>({});

  // Preload images
  useEffect(() => {
    reviews.forEach(review => {
      const img = new Image();
      img.src = review.image;
      img.onload = () => {
        setLoadedImages(prev => ({...prev, [review.image]: true}));
      };
      img.onerror = () => {
        // After 2 seconds, if image hasn't loaded, mark as failed
        setTimeout(() => {
          setLoadedImages(prev => ({...prev, [review.image]: false}));
        }, 2000);
      };
    });
  }, [reviews]);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, name: string) => {
    const target = e.target as HTMLImageElement;
    const nameParts = name.split(' ');
    const initials = nameParts.length > 1 
      ? `${nameParts[0][0]}${nameParts[1][0]}` 
      : nameParts[0].substring(0, 2);
    
    // Generate a deterministic color based on the name
    const colors = ['#4285F4', '#DB4437', '#F4B400', '#0F9D58', '#9C27B0', '#FF6D00'];
    const colorIndex = name.length % colors.length;
    const backgroundColor = colors[colorIndex];
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Draw background circle
      ctx.fillStyle = backgroundColor;
      ctx.beginPath();
      ctx.arc(100, 100, 100, 0, 2 * Math.PI);
      ctx.fill();
      
      // Add text
      ctx.font = 'bold 80px Arial';
      ctx.fillStyle = '#FFFFFF';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initials.toUpperCase(), 100, 100);
      
      // Replace the image source with the canvas data URL
      target.src = canvas.toDataURL('image/png');
    }
  };

  return (
    <div className="relative">
      <div className="testimonials-swiper">
        <Swiper
          spaceBetween={16}
          slidesPerView={1.2}
          centeredSlides={true}
          pagination={{ 
            clickable: true,
            el: '.testimonials-pagination'
          }}
          loop={true}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true
          }}
          speed={1000}
          modules={[Pagination, Autoplay]}
        >
          {reviews.map((review, index) => (
            <SwiperSlide key={`review-slide-${index}`}>
              <div 
                className="testimonial-card"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReviewClick(review, e);
                }}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    onReviewClick(review, e as any);
                  }
                }}
                style={{
                  minHeight: '280px',
                  maxHeight: '320px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div className="testimonial-content" style={{ padding: '0.75rem 0.85rem' }}>
                  <div className="review-stars">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={i < review.rating ? 'fas fa-star' : 'far fa-star'}></i>
                    ))}
                    <a 
                      href={review.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="google-source-indicator" 
                      title={language === 'en' ? 'View on Google Maps' : 'Ver no Google Maps'}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <img src="/images/svg/Google__G__logo.svg" alt="Google" className="google-icon" />
                    </a>
                  </div>
                  <div className="testimonial-text-container">
                    <p className="testimonial-text" style={{ height: '70px', maxHeight: '70px', WebkitLineClamp: 3 }}>
                      {language === 'en' ? review.comment.en : review.comment.pt}
                    </p>
                    <button 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onReviewClick(review, e);
                      }} 
                      className="read-more-btn"
                      aria-label="Read full review"
                    >
                      {language === 'en' ? 'Read More' : 'Ler Mais'}
                    </button>
                  </div>
                </div>
                <div className="testimonial-author" style={{ padding: '0.5rem 0.85rem', marginTop: 'auto' }}>
                  <div className="author-image">
                    <img 
                      src={review.image} 
                      alt={review.name}
                      onError={(e) => handleImageError(e, review.name)}
                    />
                  </div>
                  <div className="author-info">
                    <h4 className="author-name">{review.name}</h4>
                    <span className="author-location">{language === 'en' ? review.location.en : review.location.pt}</span>
                  </div>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="testimonials-pagination" style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        marginTop: '20px' 
      }}></div>
      
      <style dangerouslySetInnerHTML={{
        __html: `
          .testimonials-pagination {
            display: flex;
            justify-content: center;
            margin-top: 20px;
          }
          
          .testimonials-pagination .swiper-pagination-bullet {
            width: 10px;
            height: 10px;
            margin: 0 5px;
            border-radius: 50%;
            background-color: #ccc;
            opacity: 0.7;
            transition: all 0.3s ease;
            cursor: pointer;
          }
          
          .testimonials-pagination .swiper-pagination-bullet-active {
            transform: scale(1.2);
            background-color: var(--color-pink);
            opacity: 1;
          }

          .testimonial-card {
            flex: 0 0 auto;
            background-color: white;
            border-radius: 8px;
            border: 1px solid var(--color-pink);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
            overflow: hidden;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            flex-direction: column;
            min-height: 280px !important;
            max-height: 320px !important;
            height: auto !important;
          }
          
          .testimonial-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
            border: 1px solid var(--color-dark-pink);
          }
          
          .testimonial-card .author-image {
            width: 34px;
            height: 34px;
            min-width: 34px;
            min-height: 34px;
            border-radius: 50%;
            overflow: hidden;
            margin-right: 0.75rem;
            flex-shrink: 0;
            background-color: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            position: relative;
          }
          
          .testimonial-card .author-image img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            position: absolute;
            top: 0;
            left: 0;
          }
          
          .author-initials {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: 600;
            font-size: 14px;
            color: #fff;
            background-color: var(--color-pink);
          }
          
          .testimonial-card .review-stars {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            width: 100%;
            position: relative;
            margin-bottom: 0.5rem;
            color: var(--color-pink);
            font-size: 0.9rem;
            gap: 5px;
          }
          
          .testimonial-card .google-source-indicator {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            position: absolute;
            right: 0;
            top: 50%;
            transform: translateY(-50%);
            background-color: white;
            border-radius: 4px;
            padding: 3px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            transition: all 0.2s ease;
          }
          
          .testimonial-card .google-source-indicator:hover {
            transform: translateY(-50%) scale(1.1);
            box-shadow: 0 3px 6px rgba(0,0,0,0.15);
          }
          
          .testimonial-card .google-icon {
            width: 20px;
            height: 20px;
            object-fit: contain;
          }
          
          .testimonial-text {
            font-size: 0.92rem;
            line-height: 1.5;
            color: #4a4a4a;
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            margin-bottom: 0.5rem;
            height: 70px !important;
            max-height: 70px !important;
          }
          
          .read-more-btn {
            color: var(--color-pink);
            background: none;
            border: none;
            padding: 0;
            font: inherit;
            cursor: pointer;
            text-decoration: underline;
            font-size: 0.85rem;
            font-weight: 500;
            margin-top: 5px;
            visibility: visible;
            opacity: 1;
          }
          
          .read-more-btn:hover {
            color: var(--color-green);
          }
          
          @media (max-width: 576px) {
            .testimonial-card .review-stars {
              gap: 3px;
              font-size: 0.8rem;
            }
            
            .testimonial-card .google-icon {
              width: 16px;
              height: 16px;
            }
            
            .testimonial-card .google-source-indicator {
              padding: 2px;
              right: 0;
            }
            
            .testimonial-text {
              font-size: 0.9rem;
            }
          }

          .testimonials-swiper .swiper-slide {
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 0.5rem 0;
            transition: transform 0.8s ease, opacity 0.8s ease;
          }

          .testimonials-swiper .swiper-slide-active {
            transform: scale(1.05);
            z-index: 1;
          }

          .testimonials-swiper .testimonial-card {
            min-height: 280px !important;
            max-height: 320px !important;
          }

          .testimonials-swiper .testimonial-content {
            padding: 0.75rem 0.85rem;
          }

          .testimonials-swiper .testimonial-author {
            padding: 0.5rem 0.85rem;
            margin-top: auto;
          }
        `
      }} />
    </div>
  );
};

export default SwiperTestimonials; 